<template>
    <div class="ClientListPage">
        <DashboardSectionHeader title="Clients">
            <Button class="primary" to="/company/clients/create">Add Client</Button>
        </DashboardSectionHeader>

        <DashboardFilterHeader type="client" @change="handleFilterChange"/>

        <DashboardList
            :columns="['Client', 'Projects']"
            :isLoading="isFetching"
            :isComplete="isComplete"
            @loadMore="handleLoadMore"
        >
            <DashboardListItem
                v-for="client in clients"
                :key="client.id"
                :columns="[client.name, client.projectCount]"
                :disabled="!client.isActive"
                :actions="[
                    { label: 'Edit', click: () => $router.push(`/company/clients/${client.id}/edit`) },
                    { label: client.isActive ? 'Disable' : 'Enable', click: () => handleSetIsActive(client, !client.isActive) },
                    { label: 'Delete', variant: 'danger', click: () => handleDelete(client) },
                ]"
            />
        </DashboardList>
    </div>
</template>

<script>
import * as api from '@/api';
import DashboardFilterHeader from '@/components/DashboardFilterHeader';
import DashboardSectionHeader from '@/components/DashboardSectionHeader';
import DashboardList from '@/components/DashboardList';
import DashboardListItem from '@/components/DashboardListItem';
import ConfirmModal from '@/components/modals/ConfirmModal';

export default {
    name: 'ClientListPage',

    components: {
        DashboardFilterHeader,
        DashboardSectionHeader,
        DashboardList,
        DashboardListItem,
        ConfirmModal,
    },

    data: () => ({
        clients: [],
        page: 0,
        isFetching: false,
        isComplete: false,

        filters: null,
    }),

    mounted() {
        this.fetchClients();
    },

    methods: {
        async fetchClients(shouldResetResults = false) {
            if (this.isFetching) return;

            // Restart pagination
            if (shouldResetResults) {
                this.page = 0;
            }

            const companyId = this.$store.getters['auth/company'].id;
            const limit = 100;
            const offset = limit * this.page;

            this.isFetching = true;
            const res = await api.getClients({
                ...(this.filters ?? {}),
                company: companyId,
                limit,
                offset,
                projectCount: true,
            });
            this.isFetching = false;

            if (!res.ok) {
                return this.$awn.alert('Failed to load clients');
            }

            this.isComplete = res.body.length < limit;

            // Avoid appending to the list if we're filtering
            if (shouldResetResults) {
                this.clients = res.body;
            } else {
                this.clients = this.clients.concat(res.body);
            }
        },

        handleLoadMore() {
            this.page += 1;
            this.fetchClients();
        },

        async handleSetIsActive(client, isActive) {
            const res = await api.setIsActiveClient(client, isActive);
            if (res.error) return this.$awn.alert('Failed to update client status');

            client.isActive = res.body.isActive;
            this.$store.dispatch('company/fetchOverview');
        },

        handleDelete(client) {
            const title = 'Delete Confirmation';
            const message = 'Are you sure you want to delete this client? This will also delete projects associated with the client.';

            const onConfirm = async () => {
                const res = await api.softDeleteClient(client);
                if (res.error) {
                    return this.$awn.alert('Failed to delete client');
                }

                this.$awn.success('Client has been removed');
                this.clients = this.clients.filter(_client => _client.id !== client.id);
            };

            this.$modal.show(
                ConfirmModal,
                { title, message, type: 'delete', confirmLabel: 'Delete', onConfirm },
                { height: 'auto' },
            );
        },

        async handleFilterChange(filters) {
            this.filters = filters;
            await this.fetchClients(true);
        },
    },
}
</script>
